import registryJson from "./locale-registry.generated.json";

export type Direction = "rtl" | "ltr";
export type UiCapability = "enabled" | "planned";
export type PlatformCapability = "ui" | "translation" | "stt" | "tts" | "liveCaption" | "transliteration" | "ocr";
export type CapabilityState = "supported" | "unsupported" | "unverified";
export type LocaleCapabilities = Record<PlatformCapability, CapabilityState>;
export type PlatformLocaleTag =
  | "fa" | "en" | "ar" | "zh-Hans" | "es" | "fr" | "de" | "ru" | "pt" | "hi"
  | "ur" | "bn" | "ja" | "ko" | "tr" | "it" | "nl" | "id" | "ms" | "th"
  | "vi" | "fil" | "pa-Guru" | "te" | "ta" | "mr" | "gu" | "kn" | "ml" | "ne"
  | "si" | "my" | "km" | "lo" | "mn-Cyrl" | "ug" | "kk" | "uz-Latn" | "tg" | "ps"
  | "ckb" | "hy" | "ka" | "he" | "el" | "ro" | "pl" | "uk" | "mk" | "sw";

export interface LocaleMetadata {
  languageTag: PlatformLocaleTag;
  baseLanguage: string;
  nativeName: string;
  englishName: string;
  family: string;
  script: string;
  region: string | null;
  locale: string;
  direction: Direction;
  calendar: string;
  numberingSystem: string;
  defaultTimeZone: string | null;
  fontStack: string;
  uiCapability: UiCapability;
  capabilities: LocaleCapabilities;
}

export interface LanguagePreferences {
  uiLanguage: PlatformLocaleTag;
  contentLanguage: PlatformLocaleTag;
  courseLanguage: PlatformLocaleTag;
  examLanguage: PlatformLocaleTag;
  preferredTranslationLanguage: PlatformLocaleTag;
  preferredSubtitleLanguage: PlatformLocaleTag;
  preferredCommunicationLanguage: PlatformLocaleTag;
}

export const PLATFORM_LOCALES = registryJson.platformLocales as LocaleMetadata[];
export const PLATFORM_LOCALE_TAGS = PLATFORM_LOCALES.map((entry) => entry.languageTag);
export const INITIAL_ENABLED_LOCALES = registryJson.initiallyEnabled as PlatformLocaleTag[];
export const PLATFORM_DEFAULT_LOCALE = registryJson.platformDefault as PlatformLocaleTag;
export const QA_PSEUDO_LOCALES = registryJson.qaPseudoLocales;

const byTag = new Map(PLATFORM_LOCALES.map((entry) => [entry.languageTag.toLowerCase(), entry]));
const byBase = new Map<string, LocaleMetadata[]>();
for (const entry of PLATFORM_LOCALES) {
  const values = byBase.get(entry.baseLanguage) ?? [];
  values.push(entry);
  byBase.set(entry.baseLanguage, values);
}

export const normalisePlatformLocale = (raw: unknown): PlatformLocaleTag | null => {
  if (typeof raw !== "string" || !raw.trim()) return null;
  const candidate = raw.trim().replace(/_/g, "-");
  let canonical = candidate;
  try {
    canonical = Intl.getCanonicalLocales(candidate)[0] ?? candidate;
  } catch {
    return null;
  }
  const exact = byTag.get(canonical.toLowerCase());
  if (exact) return exact.languageTag;
  const parts = canonical.split("-");
  // An explicit unsupported script must never silently collapse to another
  // script (for example uz-Cyrl -> uz-Latn).
  if (parts.slice(1).some((part) => /^[A-Za-z]{4}$/.test(part))) return null;
  const base = parts[0]?.toLowerCase();
  const variants = base ? byBase.get(base) : undefined;
  return variants?.length === 1 ? variants[0]!.languageTag : null;
};

export const localeMetadata = (raw: unknown): LocaleMetadata | null => {
  const tag = normalisePlatformLocale(raw);
  return tag ? (byTag.get(tag.toLowerCase()) ?? null) : null;
};

export const directionOfLocale = (raw: unknown): Direction => localeMetadata(raw)?.direction ?? "ltr";

/** Unknown locale/provider capability is fail-closed and must never be advertised as support. */
export const localeCapabilityState = (raw: unknown, capability: PlatformCapability): CapabilityState =>
  localeMetadata(raw)?.capabilities[capability] ?? "unverified";

export const localeSupportsCapability = (raw: unknown, capability: PlatformCapability): boolean =>
  localeCapabilityState(raw, capability) === "supported";

export const tenantEnabledLocales = (requested?: readonly unknown[]): PlatformLocaleTag[] => {
  const source = requested?.length ? requested : INITIAL_ENABLED_LOCALES;
  return [...new Set(source.map(normalisePlatformLocale).filter((tag): tag is PlatformLocaleTag => Boolean(tag)))];
};

export interface UiLocaleResolution {
  explicitLocale?: unknown;
  userLocale?: unknown;
  tenantDefaultLocale?: unknown;
  platformDefaultLocale?: unknown;
  tenantEnabled?: readonly unknown[];
}

export const resolveUiLocale = (input: UiLocaleResolution = {}): PlatformLocaleTag => {
  const enabled = tenantEnabledLocales(input.tenantEnabled);
  const enabledSet = new Set(enabled);
  for (const raw of [input.explicitLocale, input.userLocale, input.tenantDefaultLocale, input.platformDefaultLocale, PLATFORM_DEFAULT_LOCALE]) {
    const tag = normalisePlatformLocale(raw);
    if (tag && enabledSet.has(tag)) return tag;
  }
  return enabled[0] ?? PLATFORM_DEFAULT_LOCALE;
};
